import axios from "axios";
import React, { Component } from "react";
import FeatherIcon from "feather-icons-react";
import ReactToPrint from "react-to-print";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import Headerpdf from "../../users/Headerpdf";

const MAX_HOURS_PER_DAY = 6;
const EXTENDED_MAX_HOURS_PER_DAY = 7;
const SEVENTH_HOUR_CLASS_THRESHOLD = 36;
const GREEDY_ATTEMPTS = 48;
const REPAIR_MAX_DEPTH = 6;
const REPAIR_MAX_NODES = 30000;
const REPAIR_TIME_LIMIT_MS = 450;
const FLEXIBLE_TEACHER_MIN_DAYS = 4;
const FLEXIBLE_REPAIR_SEEDS = 4;
const MAX_LESSON_HOURS_PER_CLASS_DAY = 4;
const STANDARD_SLOT_NUMBERS = Array.from(
    { length: MAX_HOURS_PER_DAY },
    (_, index) => index + 1
);
const EXTENDED_SLOT_NUMBERS = Array.from(
    { length: EXTENDED_MAX_HOURS_PER_DAY },
    (_, index) => index + 1
);

const slotNumbersForUnit = (unit) =>
    unit.allowSeventhHour ? EXTENDED_SLOT_NUMBERS : STANDARD_SLOT_NUMBERS;

const lessonDayKeyFor = (unit, dayId) =>
    `${dayId}-${unit.classId}-${unit.lessonId}`;

const toInteger = (value) => {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : 0;
};

const staffName = (staff) => {
    const user = staff?.user || {};
    return [user.last_name, user.family_name, user.first_name]
        .filter(Boolean)
        .join(" ");
};

const abbreviatedTeacherName = (name) => {
    const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    if (parts.length <= 1) return parts[0] || "Professeur";

    return [
        parts[0]?.slice(0, 6)+".",
        ...parts.slice(1).map((part) =>
            part
                .split("-")
                .filter(Boolean)
                .map((value) => `${value.charAt(0).toUpperCase()}.`)
                .join("-")
        ),
    ].join(" ");
};

const sectionName = (section) =>
    section?.short_entitled_section ||
    section?.entitled_section ||
    "";

const classBaseName = (sectionclass) =>
    [sectionclass?.class?.short_entitled_class, sectionclass?.title]
        .filter(Boolean)
        .join(" ") || "Classe inconnue";

const className = (sectionclass, parentSection = null) => {
    if (!sectionclass) return "Classe inconnue";
    const section = sectionclass.section || parentSection;
    return [classBaseName(sectionclass), sectionName(section)]
        .filter(Boolean)
        .join(" - ");
};

const lessonName = (lesson) =>
    lesson?.lesson?.short_entitled_lesson ||
    lesson?.lesson?.entitled_lesson ||
    "Cours";

const activeDayIds = (staff, validDayIds) =>
    [
        ...new Set(
            (staff?.daystaffs || [])
                .filter(
                    (availability) =>
                        Number(availability.actif) === 1 &&
                        validDayIds.has(String(availability.dayId))
                )
                .map((availability) => String(availability.dayId))
        ),
    ];

const assignedLessons = (staff) =>
    (staff?.sectionclasselessons || []).filter(
        (lesson) =>
            lesson?.id != null &&
            lesson?.sectionclass?.id != null &&
            toInteger(lesson?.week_hour) > 0
    );

const buildPreparationSummary = (staffs = [], days = [], sections = []) => {
    const validDayIds = new Set(days.map((day) => String(day.id)));
    const dayLabels = new Map(
        days.map((day) => [String(day.id), day.day || `Jour ${day.id}`])
    );
    const warnings = [];
    const classMap = new Map();
    const allCourseIds = new Set();
    const configuredClassTotals = new Map();
    const configuredCourseIds = new Set();

    staffs.forEach((staff) => {
        assignedLessons(staff).forEach((lesson) => {
            const courseId = String(lesson.id);
            const classId = String(lesson.sectionclass.id);
            if (configuredCourseIds.has(courseId)) return;
            configuredCourseIds.add(courseId);
            configuredClassTotals.set(
                classId,
                (configuredClassTotals.get(classId) || 0) +
                    toInteger(lesson.week_hour)
            );
        });
    });

    sections.forEach((section) => {
        (section.sectionclasses || []).forEach((sectionclass) => {
            classMap.set(String(sectionclass.id), {
                id: sectionclass.id,
                label: classBaseName(sectionclass),
                sectionLabel: sectionName(section) || "Non définie",
                courseIds: new Set(),
                teachers: new Set(),
                totalHours: 0,
            });
        });
    });

    const teachers = staffs
        .map((staff) => {
            const lessons = assignedLessons(staff);
            const availableDayIds = activeDayIds(staff, validDayIds);
            const courseIds = new Set(lessons.map((lesson) => String(lesson.id)));
            const totalHours = lessons.reduce(
                (total, lesson) => total + toInteger(lesson.week_hour),
                0
            );
            const hasExtendedClass = lessons.some(
                (lesson) =>
                    (configuredClassTotals.get(
                        String(lesson.sectionclass.id)
                    ) || 0) >
                    SEVENTH_HOUR_CLASS_THRESHOLD
            );
            const capacity =
                availableDayIds.length *
                (hasExtendedClass
                    ? EXTENDED_MAX_HOURS_PER_DAY
                    : MAX_HOURS_PER_DAY);
            const teacher = staffName(staff) || "Enseignant sans nom";

            lessons.forEach((lesson) => {
                const classId = String(lesson.sectionclass.id);
                const courseId = String(lesson.id);
                allCourseIds.add(courseId);
                if (!classMap.has(classId)) {
                    classMap.set(classId, {
                        id: lesson.sectionclass.id,
                        label: classBaseName(lesson.sectionclass),
                        sectionLabel: sectionName(
                            lesson.sectionclass.section
                        ) || "Non définie",
                        courseIds: new Set(),
                        teachers: new Set(),
                        totalHours: 0,
                    });
                }
                const summary = classMap.get(classId);
                if (!summary.courseIds.has(courseId)) {
                    summary.courseIds.add(courseId);
                    summary.totalHours += toInteger(lesson.week_hour);
                }
                summary.teachers.add(teacher);
            });

            let status = "Prêt";
            if (totalHours > 0 && availableDayIds.length === 0) {
                status = "Aucun jour disponible";
                warnings.push(
                    `${teacher} a ${totalHours}h de cours, mais aucun jour actif.`
                );
            } else if (totalHours > capacity) {
                status = "Capacité insuffisante";
                warnings.push(
                    `${teacher} a ${totalHours}h pour une capacité de ${capacity}h.`
                );
            }

            return {
                id: staff.id,
                teacher,
                courseCount: courseIds.size,
                availableDays: availableDayIds.map(
                    (dayId) => dayLabels.get(dayId) || dayId
                ),
                totalHours,
                capacity,
                status,
            };
        })
        .filter((teacher) => teacher.courseCount > 0)
        .sort((left, right) => left.teacher.localeCompare(right.teacher));

    const weeklyClassCapacity = days.length * MAX_HOURS_PER_DAY;
    const extendedWeeklyClassCapacity =
        days.length * EXTENDED_MAX_HOURS_PER_DAY;
    const classes = [...classMap.values()]
        .map((summary) => {
            let status = "Prête";
            if (summary.courseIds.size === 0) status = "Aucun cours";
            else if (summary.totalHours > extendedWeeklyClassCapacity) {
                status = "Capacité insuffisante";
                warnings.push(
                    `${summary.label} a ${summary.totalHours}h pour une capacité maximale de ${extendedWeeklyClassCapacity}h, 7e heure incluse.`
                );
            } else if (
                summary.totalHours > SEVENTH_HOUR_CLASS_THRESHOLD
            ) {
                status = "7e heure activée";
            }
            return {
                id: summary.id,
                label: summary.label,
                sectionLabel: summary.sectionLabel,
                courseCount: summary.courseIds.size,
                teacherCount: summary.teachers.size,
                totalHours: summary.totalHours,
                capacity:
                    summary.totalHours > SEVENTH_HOUR_CLASS_THRESHOLD
                        ? extendedWeeklyClassCapacity
                        : weeklyClassCapacity,
                status,
            };
        })
        .sort((left, right) => left.label.localeCompare(right.label));

    return {
        teachers,
        classes,
        warnings,
        totalCourses: allCourseIds.size,
        totalHours: classes.reduce((total, item) => total + item.totalHours, 0),
    };
};

const hash = (value) => {
    let result = 2166136261;
    const text = String(value);
    for (let index = 0; index < text.length; index += 1) {
        result ^= text.charCodeAt(index);
        result = Math.imul(result, 16777619);
    }
    return result >>> 0;
};

const buildUnits = (staffs, validDayIds) => {
    const units = [];
    const seenLessons = new Set();
    const teacherTotals = {};
    const classTotals = {};

    staffs.forEach((staff) => {
        const teacherId = staff?.id;
        if (teacherId == null) return;

        const availableDayIds = activeDayIds(staff, validDayIds);

        (staff.sectionclasselessons || []).forEach((lesson) => {
            const sectionclass = lesson?.sectionclass;
            const lessonId = lesson?.id;
            const lessonKey = `${teacherId}-${lessonId}`;
            const weeklyHours = toInteger(lesson?.week_hour);

            if (
                lessonId == null ||
                sectionclass?.id == null ||
                weeklyHours === 0 ||
                seenLessons.has(lessonKey)
            ) {
                return;
            }

            seenLessons.add(lessonKey);
            teacherTotals[teacherId] =
                (teacherTotals[teacherId] || 0) + weeklyHours;
            classTotals[sectionclass.id] =
                (classTotals[sectionclass.id] || 0) + weeklyHours;

            for (let hourIndex = 0; hourIndex < weeklyHours; hourIndex += 1) {
                units.push({
                    id: `${lessonKey}-${hourIndex}`,
                    lessonId,
                    teacherId,
                    teacher: staffName(staff),
                    classId: sectionclass.id,
                    classLabel: className(sectionclass),
                    lesson: lessonName(lesson),
                    weeklyHours,
                    availableDayIds,
                });
            }
        });
    });

    units.forEach((unit) => {
        unit.classTotalHours = classTotals[unit.classId] || 0;
        unit.allowSeventhHour =
            unit.classTotalHours > SEVENTH_HOUR_CLASS_THRESHOLD;
    });

    return { units, teacherTotals, classTotals };
};

const scheduleAttempt = (units, teacherTotals, attempt) => {
    const teacherSlots = {};
    const classSlots = {};
    const teacherDayLoads = {};
    const classDayLoads = {};
    const lessonDayLoads = {};
    const lessonDaySlots = {};
    const assignments = [];
    const unassigned = [];

    const orderedUnits = [...units].sort((left, right) => {
        const availabilityDifference =
            left.availableDayIds.length - right.availableDayIds.length;
        if (availabilityDifference !== 0) return availabilityDifference;

        const loadDifference =
            (teacherTotals[right.teacherId] || 0) -
            (teacherTotals[left.teacherId] || 0);
        if (loadDifference !== 0) return loadDifference;

        const lessonDifference = right.weeklyHours - left.weeklyHours;
        if (lessonDifference !== 0) return lessonDifference;

        return hash(`${left.id}-${attempt}`) - hash(`${right.id}-${attempt}`);
    });

    orderedUnits.forEach((unit) => {
        const candidates = [];

        unit.availableDayIds.forEach((dayId) => {
            const teacherDayKey = `${dayId}-${unit.teacherId}`;
            const classDayKey = `${dayId}-${unit.classId}`;
            const lessonDayKey = lessonDayKeyFor(unit, dayId);
            const usedTeacherSlots = teacherSlots[teacherDayKey] || new Set();
            const usedClassSlots = classSlots[classDayKey] || new Set();
            const lessonLoad = lessonDayLoads[lessonDayKey] || 0;

            slotNumbersForUnit(unit).forEach((slot) => {
                if (
                    usedTeacherSlots.has(slot) ||
                    usedClassSlots.has(slot) ||
                    lessonLoad >= MAX_LESSON_HOURS_PER_CLASS_DAY
                ) {
                    return;
                }

                const teacherLoad = teacherDayLoads[teacherDayKey] || 0;
                const flexibleTeacher =
                    unit.availableDayIds.length >=
                    FLEXIBLE_TEACHER_MIN_DAYS;
                const sameLessonSlots =
                    lessonDaySlots[lessonDayKey] || new Set();
                const adjacentToSameLesson = [...sameLessonSlots].some(
                    (usedSlot) => Math.abs(usedSlot - slot) === 1
                );
                const score =
                    teacherLoad * (flexibleTeacher ? -40 : 30) -
                    (classDayLoads[classDayKey] || 0) * 5 +
                    lessonLoad * 8 +
                    (adjacentToSameLesson ? 30 : 0) -
                    slot * (1 + unit.weeklyHours / 8) +
                    (hash(`${unit.id}-${dayId}-${slot}-${attempt}`) % 17) /
                        1000;

                candidates.push({
                    dayId,
                    slot,
                    score,
                    teacherDayKey,
                    classDayKey,
                    lessonDayKey,
                });
            });
        });

        candidates.sort(
            (left, right) =>
                right.score - left.score ||
                Number(left.dayId) - Number(right.dayId) ||
                left.slot - right.slot
        );

        const selected = candidates[0];
        if (!selected) {
            unassigned.push(unit);
            return;
        }

        if (!teacherSlots[selected.teacherDayKey]) {
            teacherSlots[selected.teacherDayKey] = new Set();
        }
        if (!classSlots[selected.classDayKey]) {
            classSlots[selected.classDayKey] = new Set();
        }

        teacherSlots[selected.teacherDayKey].add(selected.slot);
        classSlots[selected.classDayKey].add(selected.slot);
        teacherDayLoads[selected.teacherDayKey] =
            (teacherDayLoads[selected.teacherDayKey] || 0) + 1;
        classDayLoads[selected.classDayKey] =
            (classDayLoads[selected.classDayKey] || 0) + 1;
        lessonDayLoads[selected.lessonDayKey] =
            (lessonDayLoads[selected.lessonDayKey] || 0) + 1;
        if (!lessonDaySlots[selected.lessonDayKey]) {
            lessonDaySlots[selected.lessonDayKey] = new Set();
        }
        lessonDaySlots[selected.lessonDayKey].add(selected.slot);
        assignments.push({
            ...unit,
            dayId: selected.dayId,
            slot: selected.slot,
        });
    });

    return { assignments, unassigned, teacherDayLoads };
};

const assignmentKeys = (assignment) => ({
    teacher: `${assignment.dayId}-${assignment.teacherId}-${assignment.slot}`,
    sectionclass: `${assignment.dayId}-${assignment.classId}-${assignment.slot}`,
});

const createConstraintState = (assignments = []) => {
    const placements = new Map();
    const teacherOwners = new Map();
    const classOwners = new Map();

    assignments.forEach((assignment) => {
        const keys = assignmentKeys(assignment);
        placements.set(assignment.id, assignment);
        teacherOwners.set(keys.teacher, assignment);
        classOwners.set(keys.sectionclass, assignment);
    });

    return { placements, teacherOwners, classOwners };
};

const cloneConstraintState = (state) => ({
    placements: new Map(state.placements),
    teacherOwners: new Map(state.teacherOwners),
    classOwners: new Map(state.classOwners),
});

const removeConstraintPlacement = (state, assignment) => {
    const current = state.placements.get(assignment.id);
    if (!current) return;

    const keys = assignmentKeys(current);
    state.placements.delete(current.id);
    state.teacherOwners.delete(keys.teacher);
    state.classOwners.delete(keys.sectionclass);
};

const addConstraintPlacement = (state, unit, dayId, slot) => {
    const assignment = { ...unit, dayId, slot };
    const keys = assignmentKeys(assignment);
    state.placements.set(assignment.id, assignment);
    state.teacherOwners.set(keys.teacher, assignment);
    state.classOwners.set(keys.sectionclass, assignment);
};

const constraintCandidates = (unit, state, attempt) => {
    const teacherDayLoads = {};
    const lessonDayLoads = {};

    state.placements.forEach((assignment) => {
        if (String(assignment.teacherId) === String(unit.teacherId)) {
            const key = String(assignment.dayId);
            teacherDayLoads[key] = (teacherDayLoads[key] || 0) + 1;
        }
        if (
            String(assignment.lessonId) === String(unit.lessonId) &&
            String(assignment.classId) === String(unit.classId)
        ) {
            const key = lessonDayKeyFor(assignment, assignment.dayId);
            lessonDayLoads[key] = (lessonDayLoads[key] || 0) + 1;
        }
    });

    const candidates = [];
    unit.availableDayIds.forEach((dayId) => {
        slotNumbersForUnit(unit).forEach((slot) => {
            const keys = assignmentKeys({ ...unit, dayId, slot });
            const conflicts = [
                state.teacherOwners.get(keys.teacher),
                state.classOwners.get(keys.sectionclass),
            ].filter(
                (assignment, index, items) =>
                    assignment &&
                    items.findIndex((item) => item?.id === assignment.id) ===
                        index
            );
            const lessonDayKey = lessonDayKeyFor(unit, dayId);
            const displacedSameLessonHours = conflicts.filter(
                (assignment) =>
                    String(assignment.dayId) === String(dayId) &&
                    String(assignment.classId) === String(unit.classId) &&
                    String(assignment.lessonId) === String(unit.lessonId)
            ).length;
            const projectedLessonLoad =
                (lessonDayLoads[lessonDayKey] || 0) -
                displacedSameLessonHours +
                1;
            if (projectedLessonLoad > MAX_LESSON_HOURS_PER_CLASS_DAY) {
                return;
            }
            const flexibleConflictCount = conflicts.filter(
                (assignment) =>
                    assignment.availableDayIds.length >=
                    FLEXIBLE_TEACHER_MIN_DAYS
            ).length;

            candidates.push({
                dayId,
                slot,
                conflicts,
                score:
                    conflicts.length * 1000 +
                    flexibleConflictCount * -100 +
                    (teacherDayLoads[String(dayId)] || 0) * 20 +
                    (lessonDayLoads[lessonDayKey] || 0) * -8 +
                    slot / 100 +
                    (hash(
                        `${unit.id}-${dayId}-${slot}-repair-${attempt}`
                    ) %
                        17) /
                        1000,
            });
        });
    });

    return candidates.sort(
        (left, right) =>
            left.score - right.score ||
            Number(left.dayId) - Number(right.dayId) ||
            left.slot - right.slot
    );
};

const tryConstraintPlacement = (
    unit,
    state,
    context,
    depth = 0,
    visiting = new Set()
) => {
    if (
        depth > REPAIR_MAX_DEPTH ||
        context.nodes >= REPAIR_MAX_NODES ||
        Date.now() >= context.deadline ||
        visiting.has(unit.id)
    ) {
        return null;
    }

    context.nodes += 1;
    const nextVisiting = new Set(visiting);
    nextVisiting.add(unit.id);

    for (const candidate of constraintCandidates(
        unit,
        state,
        context.attempt
    )) {
        if (
            candidate.conflicts.some((conflict) =>
                nextVisiting.has(conflict.id)
            )
        ) {
            continue;
        }

        let branch = cloneConstraintState(state);
        candidate.conflicts.forEach((conflict) =>
            removeConstraintPlacement(branch, conflict)
        );
        addConstraintPlacement(branch, unit, candidate.dayId, candidate.slot);

        let solved = true;
        for (const displaced of candidate.conflicts) {
            branch = tryConstraintPlacement(
                displaced,
                branch,
                context,
                depth + 1,
                nextVisiting
            );
            if (!branch) {
                solved = false;
                break;
            }
        }

        if (solved) return branch;
        if (
            context.nodes >= REPAIR_MAX_NODES ||
            Date.now() >= context.deadline
        ) {
            break;
        }
    }

    return null;
};

const buildTeacherDayLoads = (assignments) =>
    assignments.reduce((loads, assignment) => {
        const key = `${assignment.dayId}-${assignment.teacherId}`;
        loads[key] = (loads[key] || 0) + 1;
        return loads;
    }, {});

const repairSchedule = (result, attempt) => {
    let state = createConstraintState(result.assignments);
    const initialAssignments = new Map(
        result.assignments.map((assignment) => [
            assignment.id,
            String(assignment.dayId),
        ])
    );
    const stillUnassigned = [];
    const context = {
        attempt,
        nodes: 0,
        deadline: Date.now() + REPAIR_TIME_LIMIT_MS,
    };

    const ordered = [...result.unassigned].sort(
        (left, right) =>
            left.availableDayIds.length - right.availableDayIds.length ||
            right.weeklyHours - left.weeklyHours ||
            hash(`${left.id}-${attempt}`) - hash(`${right.id}-${attempt}`)
    );

    ordered.forEach((unit) => {
        if (
            context.nodes >= REPAIR_MAX_NODES ||
            Date.now() >= context.deadline
        ) {
            stillUnassigned.push(unit);
            return;
        }

        const repaired = tryConstraintPlacement(unit, state, context);
        if (repaired) state = repaired;
        else stillUnassigned.push(unit);
    });

    const assignments = [...state.placements.values()];
    const flexibleMovedAssignments = assignments.filter(
        (assignment) =>
            assignment.availableDayIds.length >=
                FLEXIBLE_TEACHER_MIN_DAYS &&
            initialAssignments.has(assignment.id) &&
            initialAssignments.get(assignment.id) !== String(assignment.dayId)
    );
    return {
        assignments,
        unassigned: stillUnassigned,
        teacherDayLoads: buildTeacherDayLoads(assignments),
        repairStats: {
            attempted: result.unassigned.length,
            recovered: result.unassigned.length - stillUnassigned.length,
            flexibleHoursMoved: flexibleMovedAssignments.length,
            flexibleTeachersMoved: new Set(
                flexibleMovedAssignments.map((assignment) =>
                    String(assignment.teacherId)
                )
            ).size,
            nodes: context.nodes,
            limitReached:
                context.nodes >= REPAIR_MAX_NODES ||
                Date.now() >= context.deadline,
        },
    };
};

const scheduleQualityPenalty = (assignments, teacherTotals) => {
    const lessonDays = {};
    let penalty = 0;

    assignments.forEach((assignment) => {
        penalty += assignment.slot * (1 + assignment.weeklyHours / 8);
        if (assignment.slot >= 7) {
            penalty -= (teacherTotals[assignment.teacherId] || 0) * 2;
        }
        const key = lessonDayKeyFor(assignment, assignment.dayId);
        if (!lessonDays[key]) lessonDays[key] = [];
        lessonDays[key].push(assignment.slot);
    });

    Object.values(lessonDays).forEach((slots) => {
        const ordered = [...slots].sort((left, right) => left - right);
        for (let index = 1; index < ordered.length; index += 1) {
            penalty +=
                Math.max(0, ordered[index] - ordered[index - 1] - 1) * 25;
        }
    });

    return penalty;
};

const prioritizeHeavyTeachersInExtendedSlots = (
    assignments,
    teacherTotals
) => {
    const optimized = assignments.map((assignment) => ({ ...assignment }));
    const teacherSlotOwners = new Map(
        optimized.map((assignment) => [
            `${assignment.dayId}-${assignment.teacherId}-${assignment.slot}`,
            assignment,
        ])
    );

    optimized
        .filter((assignment) => assignment.slot >= 7)
        .forEach((extendedAssignment) => {
            const candidates = optimized
                .filter(
                    (assignment) =>
                        String(assignment.dayId) ===
                            String(extendedAssignment.dayId) &&
                        String(assignment.classId) ===
                            String(extendedAssignment.classId) &&
                        assignment.slot < extendedAssignment.slot &&
                        (teacherTotals[assignment.teacherId] || 0) >
                            (teacherTotals[
                                extendedAssignment.teacherId
                            ] || 0)
                )
                .sort(
                    (left, right) =>
                        (teacherTotals[right.teacherId] || 0) -
                        (teacherTotals[left.teacherId] || 0)
                );

            const candidate = candidates.find(
                (assignment) =>
                    !teacherSlotOwners.has(
                        `${assignment.dayId}-${assignment.teacherId}-${extendedAssignment.slot}`
                    ) &&
                    !teacherSlotOwners.has(
                        `${extendedAssignment.dayId}-${extendedAssignment.teacherId}-${assignment.slot}`
                    )
            );
            if (!candidate) return;

            teacherSlotOwners.delete(
                `${candidate.dayId}-${candidate.teacherId}-${candidate.slot}`
            );
            teacherSlotOwners.delete(
                `${extendedAssignment.dayId}-${extendedAssignment.teacherId}-${extendedAssignment.slot}`
            );
            const standardSlot = candidate.slot;
            candidate.slot = extendedAssignment.slot;
            extendedAssignment.slot = standardSlot;
            teacherSlotOwners.set(
                `${candidate.dayId}-${candidate.teacherId}-${candidate.slot}`,
                candidate
            );
            teacherSlotOwners.set(
                `${extendedAssignment.dayId}-${extendedAssignment.teacherId}-${extendedAssignment.slot}`,
                extendedAssignment
            );
        });

    return optimized;
};

const generateSchedule = (staffs = [], days = [], generation = 0) => {
    const validDayIds = new Set(days.map((day) => String(day.id)));
    const { units, teacherTotals } = buildUnits(staffs, validDayIds);
    let best = null;
    const repairSeeds = [];

    for (let attempt = 0; attempt < GREEDY_ATTEMPTS; attempt += 1) {
        const result = scheduleAttempt(
            units,
            teacherTotals,
            attempt + generation * GREEDY_ATTEMPTS
        );
        repairSeeds.push(result);
        result.qualityPenalty = scheduleQualityPenalty(
            result.assignments,
            teacherTotals
        );
        if (
            !best ||
            result.unassigned.length < best.unassigned.length ||
            (result.unassigned.length === best.unassigned.length &&
                result.qualityPenalty < best.qualityPenalty)
        ) {
            best = result;
        }
    }

    if (best?.unassigned.length > 0) {
        const seeds = repairSeeds
            .sort(
                (left, right) =>
                    left.unassigned.length - right.unassigned.length
            )
            .slice(0, FLEXIBLE_REPAIR_SEEDS);

        for (let index = 0; index < seeds.length; index += 1) {
            const repaired = repairSchedule(
                seeds[index],
                generation * GREEDY_ATTEMPTS + GREEDY_ATTEMPTS + index
            );
            repaired.qualityPenalty = scheduleQualityPenalty(
                repaired.assignments,
                teacherTotals
            );
            if (
                repaired.unassigned.length < best.unassigned.length ||
                (repaired.unassigned.length === best.unassigned.length &&
                    repaired.qualityPenalty < best.qualityPenalty)
            ) {
                best = repaired;
            }
            if (best.unassigned.length === 0) break;
        }
    }

    const assignments = prioritizeHeavyTeachersInExtendedSlots(
        best?.assignments || [],
        teacherTotals
    );
    return {
        ...(best || {
            assignments: [],
            unassigned: units,
            teacherDayLoads: {},
        }),
        assignments,
        teacherDayLoads: buildTeacherDayLoads(assignments),
        totalHours: units.length,
        teacherTotals,
        repairStats: best?.repairStats || null,
    };
};

const buildTeacherReadjustmentSuggestions = (
    staffs = [],
    days = [],
    proposal = {}
) => {
    const validDayIds = new Set(days.map((day) => String(day.id)));
    const dayLabels = new Map(
        days.map((day) => [String(day.id), day.day || `Jour ${day.id}`])
    );
    const allUnits = [
        ...(proposal.assignments || []),
        ...(proposal.unassigned || []),
    ];

    return staffs
        .map((staff) => {
            const lessons = assignedLessons(staff);
            if (lessons.length === 0) return null;

            const teacherId = String(staff.id);
            const availableDayIds = activeDayIds(staff, validDayIds);
            const teacherUnits = allUnits.filter(
                (unit) => String(unit.teacherId) === teacherId
            );
            const missingUnits = (proposal.unassigned || []).filter(
                (unit) => String(unit.teacherId) === teacherId
            );
            const weeklyHours = lessons.reduce(
                (total, lesson) => total + toInteger(lesson.week_hour),
                0
            );
            const dayLoads = availableDayIds.map((dayId) => ({
                dayId,
                label: dayLabels.get(dayId) || dayId,
                hours:
                    (proposal.teacherDayLoads || {})[
                        `${dayId}-${staff.id}`
                    ] || 0,
            }));
            const sortedLoads = [...dayLoads].sort(
                (left, right) =>
                    left.hours - right.hours ||
                    left.label.localeCompare(right.label)
            );
            const lightest = sortedLoads[0];
            const busiest = sortedLoads[sortedLoads.length - 1];
            const flexible =
                availableDayIds.length >= FLEXIBLE_TEACHER_MIN_DAYS;
            let status = "Équilibré";
            let suggestion =
                "Aucun réajustement hebdomadaire nécessaire.";

            if (missingUnits.length > 0) {
                const missingByLesson = {};
                missingUnits.forEach((unit) => {
                    missingByLesson[unit.lesson] =
                        (missingByLesson[unit.lesson] || 0) + 1;
                });
                const lessonDetails = Object.entries(missingByLesson)
                    .map(([lesson, hours]) => `${lesson}: ${hours}h`)
                    .join(", ");
                status = "À réajuster";
                suggestion = availableDayIds.length === 0
                    ? `Activer au moins un jour ou réaffecter ${missingUnits.length}h (${lessonDetails}).`
                    : `Réaffecter ${missingUnits.length}h non placée(s) vers un autre professeur/créneau, ou corriger l'hebdo concerné (${lessonDetails}).`;
            } else if (
                flexible &&
                lightest &&
                busiest &&
                busiest.hours - lightest.hours > 1
            ) {
                const hoursToMove = Math.floor(
                    (busiest.hours - lightest.hours) / 2
                );
                status = "Répartition conseillée";
                suggestion = `Déplacer environ ${hoursToMove}h de ${busiest.label} vers ${lightest.label}, selon les créneaux libres des classes affectées.`;
            } else if (flexible) {
                suggestion = `Hebdo réparti sur ${dayLoads.filter((item) => item.hours > 0).length}/${availableDayIds.length} jours disponibles; aucun déplacement prioritaire.`;
            } else if (teacherUnits.length > 0) {
                suggestion = `Disponibilité limitée à ${availableDayIds.length} jour(s); conserver cette répartition tant qu'aucune heure ne reste à placer.`;
            }

            return {
                id: staff.id,
                teacher: staffName(staff) || "Enseignant sans nom",
                weeklyHours,
                scheduledHours: teacherUnits.length - missingUnits.length,
                availableDayCount: availableDayIds.length,
                dayLoads,
                status,
                suggestion,
            };
        })
        .filter(Boolean)
        .filter(
            (item) =>
                item.availableDayCount >= FLEXIBLE_TEACHER_MIN_DAYS ||
                item.scheduledHours < item.weeklyHours
        )
        .sort((left, right) => left.teacher.localeCompare(right.teacher));
};

const HISTORY_STORAGE_KEY = "schedule-proposition-history-v1";
const MAX_HISTORY_ITEMS = 12;

const createSchedulePayloads = (proposal, periodeId) =>
    (proposal?.assignments || []).map((assignment) => ({
        sectionclasselessonId: assignment.lessonId,
        staffId: assignment.teacherId,
        dayId: assignment.dayId,
        periodeId: periodeId || null,
        date_start: null,
        date_end: null,
        time_start: null,
        time_end: null,
        hour: assignment.slot,
        operation: "Ajouter",
    }));

const safeSheetName = (label, usedNames = new Set()) => {
    const base =
        String(label || "Horaire")
            .replace(/[\\\\/?*[\\]:]/g, " ")
            .replace(/\\s+/g, " ")
            .trim()
            .slice(0, 28) || "Horaire";
    let name = base;
    let suffix = 2;
    while (usedNames.has(name)) {
        name = `${base.slice(0, 25)} ${suffix}`;
        suffix += 1;
    }
    usedNames.add(name);
    return name;
};

class ScheduleProposition extends Component {
    state = {
        loading: true,
        error: "",
        periode: null,
        periodes: [],
        sections: [],
        days: [],
        staffs: [],
        proposal: {
            assignments: [],
            unassigned: [],
            teacherDayLoads: {},
            teacherTotals: {},
            totalHours: 0,
        },
        generation: 0,
        history: [],
        generationStatus: "Terminé",
        generationDuration: 0,
        isSaving: false,
        savedSignature: "",
        saveProgress: { done: 0, total: 0, failed: 0 },
        search: "",
        sectionFilter: "all",
        teacherFilter: "all",
        sortBy: "class-asc",
    };

    componentDidMount() {
        this.loadHistory();
        this.loadData();
    }

    handleCheck = (permission) => {
        try {
            const permissions = JSON.parse(
                localStorage.getItem("permissions") || "[]"
            );
            return permissions.some((item) => item.permission === permission);
        } catch (error) {
            return false;
        }
    };

    proposalSignature = () =>
        JSON.stringify({
            periode: this.state.periode,
            assignments: this.state.proposal.assignments
                .map((item) => [item.id, item.dayId, item.slot])
                .sort((left, right) => String(left[0]).localeCompare(String(right[0]))),
        });

    loadHistory = () => {
        try {
            const history = JSON.parse(
                localStorage.getItem(HISTORY_STORAGE_KEY) || "[]"
            );
            this.setState({ history: Array.isArray(history) ? history : [] });
        } catch (error) {
            this.setState({ history: [] });
        }
    };

    archiveProposal = (proposal, generation, periode = this.state.periode) => {
        const item = {
            id: `${Date.now()}-${generation}`,
            createdAt: new Date().toISOString(),
            generation,
            periode,
            periodLabel:
                this.state.periodes.find(
                    (period) => String(period.id) === String(periode)
                )?.periode || "",
            proposal,
        };
        this.setState((state) => {
            const history = [item, ...state.history].slice(
                0,
                MAX_HISTORY_ITEMS
            );
            try {
                localStorage.setItem(
                    HISTORY_STORAGE_KEY,
                    JSON.stringify(history)
                );
            } catch (error) {
                // L'horaire reste utilisable si le stockage du navigateur est plein.
            }
            return { history };
        });
    };

    loadData = async () => {
        this.setState({ loading: true, error: "" });

        try {
            const [periodes, sections, days, staffs] = await Promise.all([
                axios.get("periodes"),
                axios.get("sections"),
                axios.get("days"),
                axios.get("staffs"),
            ]);
            const sortedDays = [...(days.data || [])].sort(
                (left, right) => Number(left.id) - Number(right.id)
            );
            const activePeriod =
                (periodes.data || []).find(
                    (period) => Number(period.statut) === 1
                ) || (periodes.data || [])[0];
            const proposal = generateSchedule(
                staffs.data || [],
                sortedDays,
                this.state.generation
            );

            this.setState({
                periodes: periodes.data || [],
                sections: sections.data || [],
                days: sortedDays,
                staffs: staffs.data || [],
                periode: activePeriod?.id || null,
                proposal,
                loading: false,
                generationStatus: "Terminé",
            }, () => this.archiveProposal(proposal, this.state.generation, activePeriod?.id));
        } catch (error) {
            this.setState({
                loading: false,
                error:
                    error.response?.data?.msg ||
                    "Impossible de charger les données nécessaires à la proposition.",
            });
        }
    };

    regenerate = () => {
        this.setState({ generationStatus: "Génération en cours", error: "" });
        window.setTimeout(() => {
            const startedAt = Date.now();
            const generation = this.state.generation + 1;
            const proposal = generateSchedule(
                this.state.staffs,
                this.state.days,
                generation
            );
            this.setState(
                {
                    generation,
                    proposal,
                    generationStatus: "Terminé",
                    generationDuration: Date.now() - startedAt,
                    savedSignature: "",
                },
                () => this.archiveProposal(proposal, generation)
            );
        }, 30);
    };

    restoreHistory = (item) => {
        this.setState({
            proposal: item.proposal,
            generation: item.generation,
            periode: item.periode || this.state.periode,
            generationStatus: "Historique restauré",
            savedSignature: "",
            error: "",
        });
    };

    deleteHistory = (id) => {
        this.setState((state) => {
            const history = state.history.filter((item) => item.id !== id);
            try {
                localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
            } catch (error) {
                // La suppression reste effective pour la session courante.
            }
            return { history };
        });
    };

    getPeriodLabel = () =>
        this.state.periodes.find(
            (period) => String(period.id) === String(this.state.periode)
        )?.periode || "";

    getVisibleClasses = () => {
        const { sections, proposal, search, sectionFilter, teacherFilter, sortBy } =
            this.state;
        const query = search.trim().toLocaleLowerCase();
        const rows = [];

        sections.forEach((section) => {
            (section.sectionclasses || []).forEach((sectionclass) => {
                const assignments = proposal.assignments.filter(
                    (assignment) =>
                        String(assignment.classId) === String(sectionclass.id)
                );
                if (assignments.length === 0) return;
                const label = className(sectionclass, section);
                const searchable = [
                    label,
                    ...assignments.flatMap((item) => [item.lesson, item.teacher]),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLocaleLowerCase();
                if (query && !searchable.includes(query)) return;
                if (
                    sectionFilter !== "all" &&
                    String(section.id) !== String(sectionFilter)
                )
                    return;
                if (
                    teacherFilter !== "all" &&
                    !assignments.some(
                        (item) =>
                            String(item.teacherId) === String(teacherFilter)
                    )
                )
                    return;
                rows.push({ section, sectionclass, assignments, label });
            });
        });

        return rows.sort((left, right) => {
            if (sortBy === "class-desc") return right.label.localeCompare(left.label);
            if (sortBy === "hours-desc")
                return right.assignments.length - left.assignments.length;
            if (sortBy === "hours-asc")
                return left.assignments.length - right.assignments.length;
            return left.label.localeCompare(right.label);
        });
    };

    saveSchedule = async () => {
        const signature = this.proposalSignature();
        if (signature === this.state.savedSignature) {
            toast.info("Cette proposition a déjà été enregistrée.");
            return;
        }
        const payloads = createSchedulePayloads(
            this.state.proposal,
            this.state.periode
        );
        if (payloads.length === 0 || this.state.isSaving) return;
        if (
            !window.confirm(
                `Enregistrer ${payloads.length} créneaux dans l'horaire de la période ${this.getPeriodLabel()} ?`
            )
        )
            return;

        this.setState({
            isSaving: true,
            saveProgress: { done: 0, total: payloads.length, failed: 0 },
        });
        let failed = 0;
        const batchSize = 8;
        for (let index = 0; index < payloads.length; index += batchSize) {
            const results = await Promise.allSettled(
                payloads
                    .slice(index, index + batchSize)
                    .map((payload) => axios.post("schedules", payload))
            );
            failed += results.filter((result) => result.status === "rejected").length;
            this.setState({
                saveProgress: {
                    done: Math.min(index + batchSize, payloads.length),
                    total: payloads.length,
                    failed,
                },
            });
        }
        this.setState({ isSaving: false });
        if (failed > 0) {
            toast.error(`${payloads.length - failed} créneaux enregistrés, ${failed} en échec.`);
        } else {
            this.setState({ savedSignature: signature });
            toast.success(`${payloads.length} créneaux enregistrés avec succès.`);
        }
    };

    buildClassSheet = (item) => {
        const maxSlot = item.assignments.some((assignment) => assignment.slot === 7)
            ? EXTENDED_MAX_HOURS_PER_DAY
            : MAX_HOURS_PER_DAY;
        const rows = [
            [`Horaire — ${item.label}`],
            [`Période : ${this.getPeriodLabel()}`],
            [],
            ["Heure", ...this.state.days.map((day) => day.day)],
        ];
        for (let slot = 1; slot <= maxSlot; slot += 1) {
            rows.push([
                `${slot}e heure`,
                ...this.state.days.map((day) => {
                    const assignment = item.assignments.find(
                        (value) =>
                            String(value.dayId) === String(day.id) &&
                            value.slot === slot
                    );
                    return assignment
                        ? `${assignment.lesson}\\n${assignment.teacher || "Professeur"}`
                        : "Libre";
                }),
            ]);
        }
        const sheet = XLSX.utils.aoa_to_sheet(rows);
        sheet["!cols"] = [
            { wch: 13 },
            ...this.state.days.map(() => ({ wch: 25 })),
        ];
        sheet["!rows"] = rows.map((row, index) => ({ hpt: index >= 4 ? 34 : 22 }));
        sheet["!merges"] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: this.state.days.length } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: this.state.days.length } },
        ];
        sheet["!autofilter"] = {
            ref: `A4:${XLSX.utils.encode_col(this.state.days.length)}${rows.length}`,
        };
        return sheet;
    };

    exportExcel = (singleItem = null) => {
        const items = singleItem ? [singleItem] : this.getVisibleClasses();
        if (items.length === 0) {
            toast.info("Aucun bloc horaire à exporter.");
            return;
        }
        const workbook = XLSX.utils.book_new();
        const usedNames = new Set();
        if (!singleItem) {
            const summary = XLSX.utils.aoa_to_sheet([
                ["Classe", "Heures placées", "Professeurs", "Période"],
                ...items.map((item) => [
                    item.label,
                    item.assignments.length,
                    new Set(item.assignments.map((value) => value.teacherId)).size,
                    this.getPeriodLabel(),
                ]),
            ]);
            summary["!cols"] = [
                { wch: 32 },
                { wch: 16 },
                { wch: 14 },
                { wch: 20 },
            ];
            XLSX.utils.book_append_sheet(workbook, summary, "Synthèse");
            usedNames.add("Synthèse");
        }
        items.forEach((item) =>
            XLSX.utils.book_append_sheet(
                workbook,
                this.buildClassSheet(item),
                safeSheetName(item.label, usedNames)
            )
        );
        const fileLabel = singleItem ? singleItem.label : "horaires-filtres";
        XLSX.writeFile(
            workbook,
            `${String(fileLabel).replace(/[^a-z0-9-_]+/gi, "-") || "horaire"}.xlsx`
        );
    };

    printBlock = (item) => {
        const node = document.getElementById(`schedule-block-${item.sectionclass.id}`);
        const popup = window.open("", "_blank", "width=1100,height=800");
        if (!node || !popup) {
            toast.error("La fenêtre d'impression n'a pas pu être ouverte.");
            return;
        }
        popup.document.write(
            "<html><head><style>body{font-family:Arial;padding:24px;color:#172033}table{width:100%;border-collapse:collapse}th,td{border:1px solid #cbd5e1;padding:8px;vertical-align:top}.sp-block-actions{display:none}.sp-slot-empty{color:#94a3b8}h3{color:#163c74}</style></head><body></body></html>"
        );
        popup.document.title = item.label;
        popup.document.body.appendChild(node.cloneNode(true));
        popup.document.close();
        popup.focus();
        popup.print();
    };

    renderGenerationProcess = () => {
        const generating = this.state.generationStatus === "Génération en cours";
        const steps = [
            ["Données", "Cours, classes et disponibilités"],
            ["Contraintes", "Conflits professeurs et classes"],
            ["Optimisation", "Répartition et heures successives"],
            ["Validation", "Créneaux non placés et capacité"],
        ];
        return (
            <section className="sp-panel sp-process-panel">
                <div className="sp-section-heading">
                    <div><span className="sp-kicker">Processus automatique</span><h2>Contrôle de la génération</h2></div>
                    <span className={`sp-status-pill ${generating ? "is-running" : "is-success"}`}>
                        {generating && <span className="spinner-border spinner-border-sm me-2" />}
                        {this.state.generationStatus}
                    </span>
                </div>
                <div className="sp-process-grid">
                    {steps.map(([title, detail], index) => (
                        <div className={`sp-process-step ${generating && index > 0 ? "is-pending" : "is-done"}`} key={title}>
                            <span>{index + 1}</span><div><b>{title}</b><small>{detail}</small></div>
                        </div>
                    ))}
                </div>
                <small className="text-muted">Génération n° {this.state.generation + 1}{this.state.generationDuration > 0 ? ` — ${this.state.generationDuration} ms` : ""}</small>
            </section>
        );
    };

    renderHistory = () => (
        <section className="sp-panel">
            <div className="sp-section-heading"><div><span className="sp-kicker">Traçabilité</span><h2>Historique des générations</h2></div><span className="sp-count">{this.state.history.length}/{MAX_HISTORY_ITEMS}</span></div>
            <div className="sp-history-list">
                {this.state.history.map((item) => (
                    <div className="sp-history-item" key={item.id}>
                        <div><b>Génération n° {item.generation + 1}</b><small>{new Date(item.createdAt).toLocaleString("fr-FR")} · {item.proposal.assignments.length}/{item.proposal.totalHours}h</small></div>
                        <div className="btn-group"><button type="button" className="btn btn-sm btn-outline-primary" onClick={() => this.restoreHistory(item)}>Restaurer</button><button type="button" title="Supprimer" className="btn btn-sm btn-outline-danger" onClick={() => this.deleteHistory(item.id)}><FeatherIcon icon="trash-2" size="14" /></button></div>
                    </div>
                ))}
                {this.state.history.length === 0 && <div className="sp-empty-state">Aucune génération archivée pour le moment.</div>}
            </div>
        </section>
    );

    renderPreparationStats = (preparation) => {
        const scheduledHours = this.state.proposal.assignments.length;
        const totalHours = this.state.proposal.totalHours;
        const placementRate =
            totalHours > 0 ? Math.round((scheduledHours / totalHours) * 100) : 0;
        const stats = [
            ["users", "Enseignants", preparation.teachers.length, "blue"],
            ["home", "Classes", preparation.classes.length, "violet"],
            ["book-open", "Cours", preparation.totalCourses, "orange"],
            ["clock", "Heures / semaine", `${preparation.totalHours}h`, "cyan"],
            ["check-circle", "Taux placé", `${placementRate}%`, placementRate === 100 ? "green" : "red"],
        ];

        return (
            <>
                <div className="sp-audit-stats">
                    {stats.map(([icon, label, value, tone]) => (
                        <div className={`sp-audit-stat is-${tone}`} key={label}>
                            <span className="sp-audit-stat-icon"><FeatherIcon icon={icon} size="17" /></span>
                            <div>
                                <small>{label}</small>
                                <b>{value}</b>
                            </div>
                        </div>
                    ))}
                </div>
                {preparation.warnings.length > 0 && (
                    <div className="sp-audit-alert">
                        <span><FeatherIcon icon="alert-triangle" size="18" /></span>
                        <div>
                            <b>Contrôles avant génération</b>
                            <ul className="mb-0 mt-1">
                                {preparation.warnings.map((warning, index) => (
                                    <li key={`preparation-warning-${index}`}>{warning}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </>
        );
    };

    renderPreparationTeachers = (teachers) => (
        <section className="sp-audit-section">
            <div className="sp-audit-section-heading">
                <span className="is-blue"><FeatherIcon icon="users" size="17" /></span>
                <div><h3>Enseignants et disponibilités</h3><small>Charge prévue et capacité selon les jours ouverts</small></div>
                <em>{teachers.length}</em>
            </div>
            <div className="table-responsive sp-audit-table-wrap">
                <table className="table align-middle sp-audit-table">
                    <thead>
                        <tr>
                            <th>Enseignant</th>
                            <th>Jours disponibles</th>
                            <th className="text-center">Cours</th>
                            <th className="text-center">Heures</th>
                            <th className="text-center">Capacité</th>
                            <th>État</th>
                        </tr>
                    </thead>
                    <tbody>
                        {teachers.map((teacher) => (
                            <tr key={`preparation-teacher-${teacher.id}`}>
                                <td className="sp-audit-name">{teacher.teacher}</td>
                                <td className="sp-day-list">
                                    {teacher.availableDays.length > 0
                                        ? teacher.availableDays.map((day) => <span key={`${teacher.id}-${day}`}>{day}</span>)
                                        : "Aucun"}
                                </td>
                                <td className="text-center">
                                    {teacher.courseCount}
                                </td>
                                <td className="text-center fw-bold">
                                    {teacher.totalHours}h
                                </td>
                                <td className="text-center">
                                    {teacher.capacity}h
                                </td>
                                <td>
                                    <span className={`sp-audit-status ${
                                        teacher.status === "Prêt"
                                            ? "is-ready"
                                            : "is-danger"
                                    }`}>{teacher.status}</span>
                                </td>
                            </tr>
                        ))}
                        {teachers.length === 0 && (
                            <tr>
                                <td colSpan="6" className="text-center text-muted">
                                    Aucun enseignant avec des cours configurés.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );

    renderPreparationClasses = (classes) => (
        <section className="sp-audit-section">
            <div className="sp-audit-section-heading">
                <span className="is-violet"><FeatherIcon icon="home" size="17" /></span>
                <div><h3>Classes et volumes de cours</h3><small>Volumes attendus comparés à la capacité disponible</small></div>
                <em>{classes.length}</em>
            </div>
            <div className="table-responsive sp-audit-table-wrap">
                <table className="table align-middle sp-audit-table">
                    <thead>
                        <tr>
                            <th>Classe</th>
                            <th>Section</th>
                            <th className="text-center">Cours</th>
                            <th className="text-center">Enseignants</th>
                            <th className="text-center">Heures</th>
                            <th className="text-center">Capacité</th>
                            <th>État</th>
                        </tr>
                    </thead>
                    <tbody>
                        {classes.map((classSummary) => (
                            <tr key={`preparation-class-${classSummary.id}`}>
                                <td className="sp-audit-name">{classSummary.label}</td>
                                <td>{classSummary.sectionLabel}</td>
                                <td className="text-center">
                                    {classSummary.courseCount}
                                </td>
                                <td className="text-center">
                                    {classSummary.teacherCount}
                                </td>
                                <td className="text-center fw-bold">
                                    {classSummary.totalHours}h
                                </td>
                                <td className="text-center">
                                    {classSummary.capacity}h
                                </td>
                                <td>
                                    <span className={`sp-audit-status ${
                                        classSummary.status === "Prête"
                                            ? "is-ready"
                                            : classSummary.status ===
                                              "7e heure activée"
                                            ? "is-info"
                                            : classSummary.status === "Aucun cours"
                                            ? "is-neutral"
                                            : "is-danger"
                                    }`}>{classSummary.status}</span>
                                </td>
                            </tr>
                        ))}
                        {classes.length === 0 && (
                            <tr>
                                <td colSpan="7" className="text-center text-muted">
                                    Aucune classe configurée.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );

    renderTeacherSummary = () => {
        const { days, staffs, proposal } = this.state;
        const scheduledTeacherIds = new Set(
            proposal.assignments.map((assignment) =>
                String(assignment.teacherId)
            )
        );

        return staffs
            .filter((staff) => scheduledTeacherIds.has(String(staff.id)))
            .sort((left, right) =>
                staffName(left).localeCompare(staffName(right))
            )
            .map((staff) => {
                const totalScheduled = proposal.assignments.filter(
                    (assignment) =>
                        String(assignment.teacherId) === String(staff.id)
                ).length;

                return (
                    <tr key={`teacher-${staff.id}`}>
                        <td>{staffName(staff)}</td>
                        {days.map((day) => {
                            const hours =
                                proposal.teacherDayLoads[
                                    `${day.id}-${staff.id}`
                                ] || 0;
                            return (
                                <td
                                    key={`teacher-${staff.id}-day-${day.id}`}
                                    className={
                                        hours === MAX_HOURS_PER_DAY
                                            ? "fw-bold text-primary"
                                            : ""
                                    }
                                >
                                    {hours > 0 ? `${hours}h` : "—"}
                                </td>
                            );
                        })}
                        <td className="fw-bold">{totalScheduled}h</td>
                    </tr>
                );
            });
    };

    renderReadjustmentSuggestions = () => {
        const suggestions = buildTeacherReadjustmentSuggestions(
            this.state.staffs,
            this.state.days,
            this.state.proposal
        );

        return (
            <section className="sp-audit-section sp-audit-suggestions">
                <div className="sp-audit-section-heading">
                    <span className="is-orange"><FeatherIcon icon="sliders" size="17" /></span>
                    <div><h3>Réajustement des hebdos professeurs</h3><small>Pistes calculées à partir de la répartition obtenue</small></div>
                    <em>{suggestions.length}</em>
                </div>
                <div className="table-responsive sp-audit-table-wrap">
                    <table className="table align-middle sp-audit-table">
                        <thead>
                            <tr>
                                <th>Professeur</th>
                                <th className="text-center">Hebdo</th>
                                <th className="text-center">Placé</th>
                                <th>Répartition obtenue</th>
                                <th>Suggestion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {suggestions.map((item) => (
                                <tr key={`readjustment-${item.id}`}>
                                    <td className="sp-audit-name">{item.teacher}</td>
                                    <td className="text-center fw-bold">
                                        {item.weeklyHours}h
                                    </td>
                                    <td
                                        className={`text-center fw-bold ${
                                            item.scheduledHours ===
                                            item.weeklyHours
                                                ? "text-success"
                                                : "text-danger"
                                        }`}
                                    >
                                        {item.scheduledHours}h
                                    </td>
                                    <td>
                                        {item.dayLoads
                                            .filter((load) => load.hours > 0)
                                            .map(
                                                (load) =>
                                                    `${load.label}: ${load.hours}h`
                                            )
                                            .join(", ") || "Aucune heure"}
                                    </td>
                                    <td>
                                        <b className={`sp-audit-status ${
                                            item.status === "À réajuster"
                                                ? "is-danger"
                                                : item.status ===
                                                  "Répartition conseillée"
                                                ? "is-warning"
                                                : "is-ready"
                                        }`}>{item.status}</b>
                                        <span className="sp-suggestion-copy">{item.suggestion}</span>
                                    </td>
                                </tr>
                            ))}
                            {suggestions.length === 0 && (
                                <tr>
                                    <td
                                        colSpan="5"
                                        className="text-center text-muted"
                                    >
                                        Aucun réajustement hebdomadaire à
                                        proposer.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        );
    };

    renderClassSchedules = () => {
        const { sections, days, proposal } = this.state;

        return [...sections]
            .sort((left, right) =>
                String(left.short_entitled_section || "").localeCompare(
                    String(right.short_entitled_section || "")
                )
            )
            .flatMap((section) =>
                [...(section.sectionclasses || [])]
                    .sort((left, right) =>
                        className(left, section).localeCompare(
                            className(right, section)
                        )
                    )
                    .map((sectionclass) => {
                        const classAssignments = proposal.assignments.filter(
                            (assignment) =>
                                String(assignment.classId) ===
                                String(sectionclass.id)
                        );
                        if (classAssignments.length === 0) return null;

                        return (
                            <li
                                className="widget-todo-item list-group-item m-2 border rounded-3 shadow"
                                key={`class-${sectionclass.id}`}
                            >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>
                                        <FeatherIcon
                                            icon="book-open"
                                            size="17"
                                            className="me-2 text-primary"
                                        />
                                        {className(sectionclass, section)}
                                    </span>
                                    <b>{classAssignments.length}h</b>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-striped table-sm mb-0">
                                        <thead>
                                            <tr>
                                                {days.map((day) => (
                                                    <th
                                                        className="border"
                                                        key={`class-${sectionclass.id}-head-${day.id}`}
                                                    >
                                                        {day.day}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                {days.map((day) => (
                                                    <td
                                                        className="border align-top"
                                                        key={`class-${sectionclass.id}-day-${day.id}`}
                                                    >
                                                        <ul className="list-unstyled mb-0">
                                                            {classAssignments
                                                                .filter(
                                                                    (assignment) =>
                                                                        String(
                                                                            assignment.dayId
                                                                        ) ===
                                                                        String(
                                                                            day.id
                                                                        )
                                                                )
                                                                .sort(
                                                                    (left, right) =>
                                                                        left.slot -
                                                                        right.slot
                                                                )
                                                                .map(
                                                                    (assignment) => (
                                                                        <li
                                                                            className="p-1 border rounded-3 mb-1"
                                                                            key={
                                                                                assignment.id
                                                                            }
                                                                        >
                                                                            <b className="text-danger me-1">
                                                                                {
                                                                                    assignment.slot
                                                                                }.
                                                                            </b>
                                                                            {
                                                                                assignment.lesson
                                                                            }
                                                                            <small className="text-primary ms-1">
                                                                                {assignment.teacher ||
                                                                                    "Professeur"}
                                                                            </small>
                                                                        </li>
                                                                    )
                                                                )}
                                                        </ul>
                                                    </td>
                                                ))}
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </li>
                        );
                    })
            );
    };

    renderLegacy() {
        const { loading, error, proposal } = this.state;
        const scheduledHours = proposal.assignments.length;
        if (loading) return <FeatherIcon className="user-search-spin" icon="loader" />;
        const preparation = buildPreparationSummary(
            this.state.staffs,
            this.state.days,
            this.state.sections
        );

        return (
            <div className="pt-4 pb-2">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 border-bottom pb-2">
                    <div>
                        <h5 className="h5 font-monospace text-primary mb-1">
                            Proposition horaire {this.getPeriodLabel()}
                        </h5>
                        <small>
                            {scheduledHours}/{proposal.totalHours}h placées —
                            base de {MAX_HOURS_PER_DAY}h/jour; 7e heure ouverte
                            uniquement aux classes dépassant leur capacité
                            standard
                        </small>
                    </div>
                    <div className="btn-group">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={this.regenerate}
                        >
                            <FeatherIcon icon="refresh-cw" size="16" /> Régénérer
                        </button>
                        <ReactToPrint
                            trigger={() => (
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    type="button"
                                >
                                    <FeatherIcon icon="printer" size="16" />{" "}
                                    Imprimer
                                </button>
                            )}
                            content={() => this.componentRef}
                        />
                    </div>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                {proposal.unassigned.length > 0 && (
                    <div className="alert alert-warning">
                        <b>
                            {proposal.unassigned.length}h n’ont pas pu être
                            placées.
                        </b>{" "}
                        Vérifiez les disponibilités des professeurs et la
                        capacité des classes, même avec leur éventuelle 7e
                        heure.
                        <ul className="mb-0 mt-1">
                            {proposal.unassigned.map((unit) => (
                                <li key={`unassigned-${unit.id}`}>
                                    {unit.teacher || "Professeur"} —{" "}
                                    {unit.lesson} ({unit.classLabel})
                                    {unit.availableDayIds.length === 0
                                        ? " : aucun jour actif"
                                        : ""}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div ref={(element) => (this.componentRef = element)}>
                    <Headerpdf />
                    <h5 className="mt-3 text-primary">
                        Données avant génération
                    </h5>
                    {this.renderPreparationStats(preparation)}
                    {this.renderPreparationTeachers(preparation.teachers)}
                    {this.renderPreparationClasses(preparation.classes)}

                    <h5 className="mt-4 text-primary">
                        Résultat de la proposition
                    </h5>
                    <h6 className="mt-3">Charge des professeurs</h6>
                    <div className="table-responsive">
                        <table className="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th>Professeur</th>
                                    {this.state.days.map((day) => (
                                        <th key={`summary-day-${day.id}`}>
                                            {day.day}
                                        </th>
                                    ))}
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>{this.renderTeacherSummary()}</tbody>
                        </table>
                    </div>

                    {this.renderReadjustmentSuggestions()}

                    <ul className="widget-todo-list-wrapper p-0">
                        {this.renderClassSchedules()}
                    </ul>
                </div>
            </div>
        );
    }

    renderAdministrativeSchedules = () => {
        const items = this.getVisibleClasses();
        return (
            <div className="sp-schedule-list">
                {items.map((item) => {
                    const maxSlot = item.assignments.some(
                        (assignment) => assignment.slot === 7
                    )
                        ? EXTENDED_MAX_HOURS_PER_DAY
                        : MAX_HOURS_PER_DAY;
                    const teacherCount = new Set(
                        item.assignments.map((assignment) => assignment.teacherId)
                    ).size;
                    return (
                        <article
                            className="sp-schedule-card"
                            id={`schedule-block-${item.sectionclass.id}`}
                            key={`administrative-${item.sectionclass.id}`}
                        >
                            <header className="sp-schedule-card-header">
                                <div>
                                    <span className="sp-kicker">Bloc horaire</span>
                                    <h3>{item.label}</h3>
                                    <small>{item.assignments.length} heures · {teacherCount} professeur{teacherCount > 1 ? "s" : ""}</small>
                                </div>
                                <div className="btn-group sp-block-actions">
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => this.printBlock(item)} title="Imprimer ce bloc"><FeatherIcon icon="printer" size="15" /> Imprimer</button>
                                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => this.exportExcel(item)} title="Exporter ce bloc"><FeatherIcon icon="download" size="15" /> Excel</button>
                                </div>
                            </header>
                            <div className="table-responsive">
                                <table className="sp-schedule-grid">
                                    <thead><tr><th>Heure</th>{this.state.days.map((day) => <th key={`admin-head-${item.sectionclass.id}-${day.id}`}>{day.day}</th>)}</tr></thead>
                                    <tbody>
                                        {Array.from({ length: maxSlot }, (_, index) => index + 1).map((slot) => (
                                            <tr key={`admin-slot-${item.sectionclass.id}-${slot}`}>
                                                <th><span>{slot}</span><small>{slot === 1 ? "1re" : `${slot}e`} heure</small></th>
                                                {this.state.days.map((day) => {
                                                    const assignment = item.assignments.find(
                                                        (value) => String(value.dayId) === String(day.id) && value.slot === slot
                                                    );
                                                    return (
                                                        <td key={`admin-cell-${item.sectionclass.id}-${day.id}-${slot}`}>
                                                            {assignment ? (
                                                                <div className="sp-slot-filled">
                                                                    <b>{assignment.lesson}</b>
                                                                    <small
                                                                        className="sp-teacher-name"
                                                                        title={assignment.teacher || "Professeur"}
                                                                    >
                                                                        <FeatherIcon icon="user" size="12" />
                                                                        <span>{abbreviatedTeacherName(assignment.teacher)}</span>
                                                                    </small>
                                                                </div>
                                                            ) : (
                                                                <div className="sp-slot-empty"><FeatherIcon icon="minus" size="14" /><span>Créneau libre</span></div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </article>
                    );
                })}
                {items.length === 0 && <div className="sp-panel sp-empty-state"><FeatherIcon icon="search" size="28" /><b>Aucun horaire ne correspond aux filtres.</b><span>Modifiez la recherche ou réinitialisez les critères.</span></div>}
            </div>
        );
    };

    render() {
        const { loading, error, proposal, saveProgress, isSaving } = this.state;
        if (loading) return <FeatherIcon className="user-search-spin" icon="loader" />;
        const preparation = buildPreparationSummary(
            this.state.staffs,
            this.state.days,
            this.state.sections
        );
        const scheduledHours = proposal.assignments.length;
        const completion = proposal.totalHours
            ? Math.round((scheduledHours / proposal.totalHours) * 100)
            : 0;
        const visibleClasses = this.getVisibleClasses();
        const scheduledTeacherIds = new Set(
            proposal.assignments.map((assignment) => assignment.teacherId)
        );
        const canSave = this.handleCheck("Ajouter horaire");
        const alreadySaved = this.proposalSignature() === this.state.savedSignature;

        return (
            <main className="schedule-proposition-page">
                <section className="sp-hero">
                    <div className="sp-hero-copy">
                        <span className="sp-eyebrow"><FeatherIcon icon="calendar" size="15" /> Administration scolaire</span>
                        <h1>Génération des horaires</h1>
                        <p>Pilotez la proposition automatique, contrôlez les contraintes puis publiez l’horaire validé.</p>
                    </div>
                    <div className="sp-hero-actions">
                        <label className="sp-period-select"><span>Période active</span><select value={this.state.periode || ""} onChange={(event) => this.setState({ periode: event.target.value, savedSignature: "" })}>{this.state.periodes.map((period) => <option value={period.id} key={period.id}>{period.periode}</option>)}</select></label>
                        <button type="button" className="btn btn-light" onClick={this.regenerate} disabled={this.state.generationStatus === "Génération en cours"}><FeatherIcon icon="refresh-cw" size="16" /> Régénérer</button>
                        <button type="button" className="btn btn-success" title={!canSave ? "Permission Ajouter horaire requise" : ""} onClick={this.saveSchedule} disabled={!canSave || isSaving || scheduledHours === 0 || alreadySaved}><FeatherIcon icon={alreadySaved ? "check" : "save"} size="16" /> {isSaving ? `${saveProgress.done}/${saveProgress.total}` : alreadySaved ? "Horaire enregistré" : "Enregistrer dans la BD"}</button>
                    </div>
                </section>

                {error && <div className="alert alert-danger mt-3">{error}</div>}
                {isSaving && <div className="sp-save-progress"><div style={{ width: `${saveProgress.total ? (saveProgress.done / saveProgress.total) * 100 : 0}%` }} /></div>}

                <section className="sp-kpi-grid">
                    <div className="sp-kpi"><span className="is-blue"><FeatherIcon icon="check-circle" /></span><div><small>Heures placées</small><b>{scheduledHours}/{proposal.totalHours}h</b></div><em>{completion}%</em></div>
                    <div className="sp-kpi"><span className="is-violet"><FeatherIcon icon="book-open" /></span><div><small>Classes planifiées</small><b>{new Set(proposal.assignments.map((item) => item.classId)).size}</b></div><em>{visibleClasses.length} affichées</em></div>
                    <div className="sp-kpi"><span className="is-green"><FeatherIcon icon="users" /></span><div><small>Professeurs mobilisés</small><b>{scheduledTeacherIds.size}</b></div><em>{this.state.days.length} jours</em></div>
                    <div className="sp-kpi"><span className={proposal.unassigned.length ? "is-red" : "is-green"}><FeatherIcon icon={proposal.unassigned.length ? "alert-triangle" : "shield"} /></span><div><small>Heures non placées</small><b>{proposal.unassigned.length}</b></div><em>{proposal.unassigned.length ? "Action requise" : "Aucun conflit"}</em></div>
                </section>

                <div className="sp-dashboard-grid">
                    {this.renderGenerationProcess()}
                    {this.renderHistory()}
                </div>

                {proposal.unassigned.length > 0 && (
                    <section className="sp-warning" role="alert">
                        <span className="sp-warning-icon"><FeatherIcon icon="alert-triangle" size="20" /></span>
                        <div className="sp-warning-content">
                            <div className="sp-warning-heading">
                                <div>
                                    <span className="sp-warning-kicker">Attention requise</span>
                                    <h2>{proposal.unassigned.length} heure{proposal.unassigned.length > 1 ? "s" : ""} non placée{proposal.unassigned.length > 1 ? "s" : ""}</h2>
                                </div>
                                <span className="sp-warning-count">{proposal.unassigned.length}h</span>
                            </div>
                            <p>Certains cours dépassent les disponibilités, les charges ou la capacité quotidienne.</p>
                            <ul>{proposal.unassigned.slice(0, 8).map((unit) => <li key={`warning-${unit.id}`}><b>{unit.teacher || "Professeur"}</b><span>{unit.lesson} · {unit.classLabel}</span></li>)}</ul>
                            {proposal.unassigned.length > 8 && <small className="sp-warning-more">+ {proposal.unassigned.length - 8} autre(s) heure(s) à contrôler</small>}
                        </div>
                    </section>
                )}

                <details className="sp-panel sp-preparation">
                    <summary>
                        <span className="sp-preparation-icon"><FeatherIcon icon="clipboard" size="19" /></span>
                        <div className="sp-preparation-title">
                            <span className="sp-kicker">Audit des entrées</span>
                            <h2>Données et contraintes avant génération</h2>
                            <small>Consultez les charges, disponibilités et capacités prises en compte.</small>
                        </div>
                        <span className="sp-preparation-toggle"><span>Afficher l’audit</span><FeatherIcon icon="chevron-down" size="18" /></span>
                    </summary>
                    {this.renderPreparationStats(preparation)}
                    {this.renderPreparationTeachers(preparation.teachers)}
                    {this.renderPreparationClasses(preparation.classes)}
                    {this.renderReadjustmentSuggestions()}
                </details>

                <section className="sp-toolbar sp-panel">
                    <div className="sp-toolbar-title"><span className="sp-kicker">Résultats</span><h2>Horaires par classe</h2></div>
                    <div className="sp-filters">
                        <label className="sp-search"><FeatherIcon icon="search" size="16" /><input type="search" value={this.state.search} placeholder="Classe, cours, professeur…" onChange={(event) => this.setState({ search: event.target.value })} /></label>
                        <select value={this.state.sectionFilter} onChange={(event) => this.setState({ sectionFilter: event.target.value })}><option value="all">Toutes les sections</option>{this.state.sections.map((section) => <option value={section.id} key={`filter-section-${section.id}`}>{sectionName(section) || `Section ${section.id}`}</option>)}</select>
                        <select value={this.state.teacherFilter} onChange={(event) => this.setState({ teacherFilter: event.target.value })}><option value="all">Tous les professeurs</option>{this.state.staffs.filter((staff) => scheduledTeacherIds.has(staff.id) || scheduledTeacherIds.has(String(staff.id))).sort((a, b) => staffName(a).localeCompare(staffName(b))).map((staff) => <option value={staff.id} key={`filter-staff-${staff.id}`}>{staffName(staff)}</option>)}</select>
                        <select value={this.state.sortBy} onChange={(event) => this.setState({ sortBy: event.target.value })}><option value="class-asc">Classe A → Z</option><option value="class-desc">Classe Z → A</option><option value="hours-desc">Plus chargé</option><option value="hours-asc">Moins chargé</option></select>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => this.setState({ search: "", sectionFilter: "all", teacherFilter: "all", sortBy: "class-asc" })}><FeatherIcon icon="x" size="15" /> Réinitialiser</button>
                    </div>
                    <div className="sp-export-actions">
                        <span>{visibleClasses.length} bloc(s)</span>
                        <ReactToPrint trigger={() => <button className="btn btn-outline-primary" type="button"><FeatherIcon icon="printer" size="16" /> Imprimer la sélection</button>} content={() => this.componentRef} />
                        <button className="btn btn-outline-success" type="button" onClick={() => this.exportExcel()}><FeatherIcon icon="file-text" size="16" /> Excel par blocs</button>
                    </div>
                </section>

                <div ref={(element) => (this.componentRef = element)} className="sp-print-zone">
                    <div className="sp-print-header"><Headerpdf /><h2>Horaires — {this.getPeriodLabel()}</h2></div>
                    {this.renderAdministrativeSchedules()}
                </div>
            </main>
        );
    }
}

export {
    abbreviatedTeacherName,
    buildPreparationSummary,
    buildTeacherReadjustmentSuggestions,
    createSchedulePayloads,
    generateSchedule,
    EXTENDED_MAX_HOURS_PER_DAY,
    MAX_HOURS_PER_DAY,
    safeSheetName,
};
export default ScheduleProposition;
